import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PlayCircle, Sparkles } from "lucide-react";
import { ColorHeader, ModalFrame } from "./FormBits";
import { FlowStrip, HowToByStyle } from "./HowToGuide";

const Modal = ({ open, onClose, label, style, play }) => (
  <AnimatePresence>
    {open && (
      <ModalFrame testId="howto-modal" onClose={onClose} zIndex={90} maxW={style === "cards" ? "max-w-2xl" : "max-w-xl"}>
        <ColorHeader kicker="Quick guide" title={label} sub="Register → Log in to Classroom → Open Video. It repeats automatically; close whenever you are ready." onClose={onClose} closeTestId="howto-close" icon={<PlayCircle size={20} />} />
        <div className="px-5 sm:px-8 py-6 bg-[#FBFAF6]" data-testid="howto-body"><HowToByStyle style={style} play={play} /></div>
      </ModalFrame>
    )}
  </AnimatePresence>
);

const WatchButton = ({ onClick, label, big }) => (
  <button type="button" data-testid="howto-open" onClick={onClick} className={`relative group inline-flex items-center gap-3 rounded-full font-semibold text-white bg-gradient-to-r from-[#C86A51] to-[#E08A5B] hover:from-[#B85C45] hover:to-[#D47C4C] shadow-[0_12px_30px_rgba(200,106,81,0.45)] hover:-translate-y-0.5 transition-[transform,box-shadow] ${big ? "pl-2 pr-6 py-2 text-base sm:text-lg" : "pl-1.5 pr-4 py-1.5 text-xs sm:text-sm"}`}>
    <span className="absolute inset-0 rounded-full ring-2 ring-[#E08A5B]/60 animate-ping opacity-60 pointer-events-none" style={{ animationDuration: "2.2s" }} />
    <motion.span animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 1.6, repeat: Infinity }} className={`grid place-items-center rounded-full bg-white text-[#C86A51] ${big ? "w-10 h-10" : "w-7 h-7"}`}><PlayCircle size={big ? 22 : 15} /></motion.span>
    <span>{label}</span>
  </button>
);

export const HowToButton = ({ content, stacked = false }) => {
  const [open, setOpen] = useState(false);
  if (content?.howto_enabled === false) return null;
  const style = content?.howto_style || "cards";
  const label = content?.howto_label || "How to open the video";
  const play = content?.howto_autoplay !== false;
  if ((content?.howto_look || "banner") === "button") {
    return <><WatchButton onClick={() => setOpen(true)} label={label} /><Modal open={open} onClose={() => setOpen(false)} label={label} style={style} play={play} /></>;
  }
  return (
    <>
      <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} data-testid="howto-banner" className="relative w-full overflow-hidden rounded-2xl text-white p-4 sm:p-5 shadow-[0_20px_50px_rgba(15,27,22,0.25)]" style={{ background: "linear-gradient(135deg,#0F1B16 0%,#1A2E26 55%,#2F6B4F 100%)" }}>
        <div className="pointer-events-none absolute -top-20 -right-16 w-56 h-56 rounded-full bg-[#C86A51]/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-10 w-52 h-52 rounded-full bg-white/10 blur-3xl" />
        <div className={`relative flex flex-col gap-4 ${stacked ? "" : "md:flex-row md:items-center"}`}>
          <div className="flex-1 min-w-0">
            <p className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.25em] text-[#F3C9A8]"><Sparkles size={11} /> New here? Start with this</p>
            <h3 className="font-serif text-xl sm:text-2xl leading-tight mt-2" data-testid="howto-banner-title">{label}</h3>
            <p className="text-xs sm:text-sm text-white/75 mt-1 leading-relaxed">Register → Log in to Classroom → Open Video. A one-minute animated guide shows every tap, so you never miss a class.</p>
          </div>
          <div className={`shrink-0 flex items-center gap-2 ${stacked ? "flex-wrap" : "md:flex-col"}`}>
            <WatchButton onClick={() => setOpen(true)} label="Watch the 3 steps" big />
            <span className="text-[10px] uppercase tracking-widest text-white/60">Takes 1 minute</span>
          </div>
        </div>
        <div className="relative mt-4" data-testid="howto-banner-strip"><FlowStrip play={play} compact /></div>
      </motion.div>
      <Modal open={open} onClose={() => setOpen(false)} label={label} style={style} play={play} />
    </>
  );
};
